import React, { useEffect, useRef, useState } from "react";
import SiteLayout from "@/components/SiteLayout";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { defaultAdminRoute } from "@/lib/permissions";
import { MessageCircle, Mail, KeyRound, ArrowLeft } from "lucide-react";

/**
 * Member / Admin login page.
 *
 * Two login channels coexist:
 *   • Email + Password  (legacy, default for both customers and admins)
 *   • WhatsApp OTP      (customer-only; uses the BSP `npc_otp_login` template)
 *
 * The admin sign-in is intentionally email-only — admin accounts may not
 * have a registered mobile, and customer-facing OTP doesn't belong on the
 * admin login surface.
 */
export default function Login({ admin = false }) {
  const { loginWithPassword, sendWhatsAppOtp, loginWithWhatsAppOtp } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();

  // method = 'whatsapp' | 'password' — WhatsApp OTP is now the primary
  // login path; email/password remains as a secondary option.
  const [method, setMethod] = useState("whatsapp");

  // Shared
  const [loading, setLoading] = useState(false);

  // Password flow
  const [email, setEmail] = useState(sp.get("email") || "");
  const [password, setPassword] = useState("");

  // WhatsApp OTP flow
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownTimer = useRef(null);

  useEffect(() => {
    if (sp.get("email")) toast.info("Use the temporary password from your purchase confirmation. You can change it after login.");
    if (sp.get("reset") === "1") toast.success("Password updated. Please sign in.");
  }, [sp]);

  // Tick down the resend cooldown so the UI can show "Resend in 23s".
  useEffect(() => {
    if (cooldown <= 0) {
      if (cooldownTimer.current) { clearInterval(cooldownTimer.current); cooldownTimer.current = null; }
      return;
    }
    cooldownTimer.current = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => { if (cooldownTimer.current) clearInterval(cooldownTimer.current); };
  }, [cooldown]);

  const routeAfter = (u) => {
    const adminRoles = ["super_admin", "admin", "accounts", "staff"];
    nav(adminRoles.includes(u.role) ? defaultAdminRoute(u.role) : "/dashboard");
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await loginWithPassword(email, password, admin);
      toast.success("Welcome back");
      routeAfter(u);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const sendOtp = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const r = await sendWhatsAppOtp(mobile);
      setOtpSent(true);
      setCooldown(r.cooldown || 30);
      if (r.delivery === "queued" || r.delivery === "sent") {
        toast.success("OTP sent to your WhatsApp");
      } else if (r.delivery === "template_not_approved") {
        toast.error("WhatsApp template not approved yet — contact support");
      } else {
        toast.info(`OTP ${r.delivery || "queued"}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send OTP");
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await loginWithWhatsAppOtp(mobile, otp);
      toast.success("Welcome back");
      routeAfter(u);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
    } finally { setLoading(false); }
  };

  const resetOtpFlow = () => {
    setOtpSent(false);
    setOtp("");
    setCooldown(0);
  };

  return (
    <SiteLayout>
      <section className="mx-auto max-w-md px-6 py-16">
        <span className="overline">{admin ? "Admin access" : "Member login"}</span>
        <h1 className="mt-2 font-display text-4xl font-bold text-[#0F2540]">
          {admin ? "Admin sign in" : "Welcome back"}
        </h1>

        {/* Method tabs — customers only. Admin login stays email-only. */}
        {!admin && (
          <div className="mt-6 inline-flex w-full rounded-full bg-[#F0F4FA] p-1 border border-[#E1E8F0]" data-testid="login-method-tabs">
            <button
              type="button"
              onClick={() => { setMethod("whatsapp"); }}
              className={`flex-1 px-4 py-2 text-xs font-semibold rounded-full transition-colors inline-flex items-center justify-center gap-2 ${method === "whatsapp" ? "bg-[#0F2540] text-white shadow-sm" : "text-[#0F2540] hover:bg-white"}`}
              data-testid="login-method-whatsapp"
            >
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp OTP
            </button>
            <button
              type="button"
              onClick={() => { setMethod("password"); resetOtpFlow(); }}
              className={`flex-1 px-4 py-2 text-xs font-semibold rounded-full transition-colors inline-flex items-center justify-center gap-2 ${method === "password" ? "bg-[#0F2540] text-white shadow-sm" : "text-[#0F2540] hover:bg-white"}`}
              data-testid="login-method-password"
            >
              <Mail className="h-3.5 w-3.5" /> Email &amp; Password
            </button>
          </div>
        )}

        {/* Email + Password form */}
        {(admin || method === "password") && (
          <form onSubmit={submitPassword} className="mt-8 space-y-4" data-testid={admin ? "admin-login-form" : "login-form"}>
            <div>
              <label className="text-sm font-medium text-[#0F2540]">{admin ? "Email or username" : "Email"}</label>
              <input type={admin ? "text" : "email"} value={email} onChange={(e) => setEmail(e.target.value)} required
                autoCapitalize="none" autoCorrect="off"
                className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]"
                data-testid="login-email" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F2540]">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]"
                data-testid="login-password" />
            </div>
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs text-[#1E4F87] underline" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
            <button disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}

        {/* WhatsApp OTP form — two-step. Step 1: ask for mobile. Step 2: ask for OTP. */}
        {!admin && method === "whatsapp" && (
          <div className="mt-8 space-y-4" data-testid="login-whatsapp-form">
            {!otpSent && (
              <form onSubmit={sendOtp} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#0F2540]">Registered mobile number</label>
                  <div className="mt-1 flex rounded-xl border border-[#E1E8F0] bg-white overflow-hidden focus-within:border-[#1E4F87]">
                    <span className="px-3 py-2.5 bg-[#F0F4FA] text-sm text-[#5C6B7A] border-r border-[#E1E8F0]">+91</span>
                    <input
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      placeholder="98765 43210"
                      required
                      className="flex-1 px-4 py-2.5 outline-none"
                      data-testid="login-whatsapp-mobile"
                    />
                  </div>
                  <p className="mt-1.5 text-[11px] text-[#5C6B7A]">
                    We&apos;ll send a 6-digit OTP to this number on WhatsApp.
                  </p>
                </div>
                <button disabled={loading || mobile.length !== 10} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="login-whatsapp-send-otp">
                  <MessageCircle className="h-4 w-4" /> {loading ? "Sending OTP…" : "Send OTP"}
                </button>
              </form>
            )}

            {otpSent && (
              <form onSubmit={verifyOtp} className="space-y-4">
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={resetOtpFlow}
                    className="inline-flex items-center gap-1 text-xs text-[#1E4F87] font-medium hover:underline"
                    data-testid="login-whatsapp-change-number"
                  >
                    <ArrowLeft className="h-3 w-3" /> Change number
                  </button>
                  <span className="text-xs text-[#5C6B7A]">OTP sent to <b>+91 {mobile}</b></span>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F2540]">Enter 6-digit OTP</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="••••••"
                    required
                    className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-3 text-center text-2xl font-mono tracking-[0.45em] outline-none focus:border-[#1E4F87]"
                    data-testid="login-whatsapp-otp"
                  />
                </div>
                <button disabled={loading || otp.length !== 6} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="login-whatsapp-verify-otp">
                  <KeyRound className="h-4 w-4" /> {loading ? "Verifying…" : "Verify &amp; sign in"}
                </button>
                <button
                  type="button"
                  onClick={sendOtp}
                  disabled={loading || cooldown > 0}
                  className="w-full text-xs text-[#1E4F87] font-semibold py-1 disabled:text-[#5C6B7A] disabled:cursor-not-allowed"
                  data-testid="login-whatsapp-resend-otp"
                >
                  {cooldown > 0 ? `Resend OTP in ${cooldown}s` : "Resend OTP"}
                </button>
              </form>
            )}
          </div>
        )}

        {!admin && (
          <p className="mt-6 text-sm text-[#5C6B7A]">
            Don&apos;t have an NPC card yet? <Link to="/plans" className="text-[#1E4F87] font-semibold" data-testid="to-plans">View plans &amp; join</Link>
          </p>
        )}
      </section>
    </SiteLayout>
  );
}
